import { ReactNode, useState } from "react";
import { useAuth } from "react-oidc-context";
import styled from "styled-components";

import { faCircleCheck, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "@ntemarked/mittne-components";
import Button, { type ButtonColors } from "@ntemarked/mittne-components/dist/Button/Button";

import TextCopyButton from "components/molecules/TextCopyButton";
import Portal from "components/organisms/Portal";
import useAccess from "hooks/useAccess";
import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { InnkjoepsbestillingText } from "pages/Sikring/components/InnkjoepsbestillingText";
import { MonthlyVolumeList } from "pages/Sikring/components/MonthlyVolumeList";
import { StatusBadge } from "pages/Sikring/components/StatusBadge";
import {
	FLYT_LABEL,
	STATUS_DESCRIPTION,
	STATUS_LABEL,
	isDefined,
	isDeletable,
	isDeleted,
	fmtDate,
	fmtDateTime,
	formatNumber,
	fmtPrice,
	fmtPriceWithUnit,
	monthLabel,
	scopeFull,
	PRICE_UNIT,
	priceOf,
	totalVolumeKwh,
	sortMonthlyVolumes,
} from "pages/Sikring/sikringUtils";
import { Kvalitetssikring, SikringFlyt, SikringOmfangType, Sikringsordre, Sikringsordrestatus } from "pages/Sikring/types";

interface SikringsordreDrawerProps {
	order: Sikringsordre;
	onClose: () => void;
}

export function SikringsordreDrawer({ order, onClose }: Readonly<SikringsordreDrawerProps>) {
	const { sikring } = order;
	const access = useAccess();
	const canDelete = access.includes("admin-order") && !isDeleted(order.status);
	// Samme rolle som backend krever for å kvalitetssikre.
	const canQualityCheck = access.includes("manage-sikring");
	// flyt er null for eldre sikringer, som alle gikk via kontrakt.
	const hasContract = sikring.flyt !== SikringFlyt.KunSikring;
	const volume = totalVolumeKwh(order);
	const monthlyVolumes = sortMonthlyVolumes(order.maanedsvolumer);

	const fields: { name: string; value: ReactNode }[] = [
		{ name: "Org.nr.", value: order.kunde?.organisasjonsnummer || "–" },
		{ name: "Kontaktperson", value: order.kunde?.kontaktperson || "–" },
		{ name: "Omfang", value: scopeFull(sikring) },
		...(order.maalepunkter?.length
			? [{ name: order.maalepunkter.length === 1 ? "Målepunkt-ID" : `Målepunkt (${order.maalepunkter.length})`, value: order.maalepunkter.join(", ") }]
			: []),
		{ name: "Flyt", value: sikring.flyt ? FLYT_LABEL[sikring.flyt] : "–" },
		{ name: "Periode", value: `${fmtDate(sikring.start)} – ${fmtDate(sikring.slutt)} (begge inklusive)` },
		{ name: "Volum", value: `${formatNumber(volume)} kWh` },
		{ name: "Kundepris (eks. mva)", value: fmtPriceWithUnit(priceOf(sikring)) },
		...(isDefined(sikring.innpris) || isDefined(sikring.fortjeneste)
			? [{ name: "Kjøpspris / margin (eks. mva)", value: `${fmtPrice(sikring.innpris)} + ${fmtPrice(sikring.fortjeneste)} ${PRICE_UNIT}` }]
			: []),
		{ name: "Innkjøpsbestilling", value: <InnkjoepsbestillingText skipEnergiMail={sikring.skipEnergiMail} created /> },
		{ name: "Opprettet", value: fmtDateTime(order.opprettet) },
		{ name: "Registrert av", value: order.opprettetAv || "–" },
		{ name: "Sist oppdatert", value: fmtDateTime(order.sistOppdatert) },
	];

	return (
		<Portal>
			<Backdrop onClick={onClose} />
			<Panel>
				<TopRow>
					<h2 style={{ margin: 0 }}>{order.kunde?.navn || "Sikringsordre"}</h2>
					<CloseLink
						href="#"
						onClick={(e) => {
							e.preventDefault();
							onClose();
						}}
					>
						<FontAwesomeIcon icon={faXmark} />
					</CloseLink>
				</TopRow>
				<div style={{ margin: "6px 0 6px" }}>
					<StatusBadge status={order.status} />
				</div>
				<StatusExplanation>{STATUS_DESCRIPTION[order.status]}</StatusExplanation>

				{order.status === Sikringsordrestatus.Failed && order.feilaarsak && (
					<FailureBox>
						<FailureTitle>Feilårsak</FailureTitle>
						{order.feilaarsak}
					</FailureBox>
				)}

				<FieldGrid>
					{fields.map((f) => (
						<Field key={f.name} {...f} />
					))}
				</FieldGrid>

				<Section>
					<SectionTitle>Månedsvolum ({monthlyVolumes.length})</SectionTitle>
					{monthlyVolumes.length === 0 ? (
						<EmptyText>Ingen månedsvolum er sendt til UtilityCloud ennå.</EmptyText>
					) : (
						<MonthlyVolumeList volumes={monthlyVolumes} />
					)}
				</Section>

				<QualityCheckSection
					orderId={order.id}
					status={order.status}
					opprettetAv={order.opprettetAv}
					kvalitetssikring={order.kvalitetssikring}
					canEdit={canQualityCheck}
				/>

				<Section>
					<details>
						<TechnicalSummary>Tekniske detaljer</TechnicalSummary>
						<TechnicalList>
							<TechnicalRow>
								<span>Ordre-ID</span>
								<Guid title={order.ordreId}>{order.ordreId}</Guid>
								<TextCopyButton text={order.ordreId} title="Kopier ordre-ID" color={colors.blue} />
							</TechnicalRow>
							{order.produktId && (
								<TechnicalRow>
									<span>Produkt-ID (UtilityCloud)</span>
									<Guid title={order.produktId}>{order.produktId}</Guid>
									<TextCopyButton text={order.produktId} title="Kopier produkt-ID" color={colors.blue} />
								</TechnicalRow>
							)}
							{monthlyVolumes.map((m) => (
								<TechnicalRow key={m.maaned}>
									<span>Korrelasjons-ID {monthLabel(m.maaned)}</span>
									<Guid title={m.correlationGuid}>{m.correlationGuid}</Guid>
									<TextCopyButton text={m.correlationGuid} title="Kopier korrelasjons-ID" color={colors.blue} />
								</TechnicalRow>
							))}
						</TechnicalList>
					</details>
				</Section>

				{canDelete && <DeleteSection orderId={order.id} deletable={isDeletable(order.status)} hasContract={hasContract} onDeleted={onClose} />}
			</Panel>
		</Portal>
	);
}

const DANGER: ButtonColors = { background: colors.coral800, backgroundPressed: colors.coral, text: colors.white, border: colors.coral800 };
const DANGER_SECONDARY: ButtonColors = { background: colors.white, backgroundPressed: colors.coral100, text: colors.coral800, border: colors.coral800 };

function deleteErrorMessage(status: number): string {
	switch (status) {
		case 404:
			return "Fant ikke sikringsordren. Den kan allerede være slettet.";
		case 409:
			return "Sikringsordren er ikke ferdig behandlet, og kan ikke slettes ennå.";
		default:
			return "Klarte ikke å slette sikringsordren. Prøv igjen senere.";
	}
}

interface DeleteSectionProps {
	orderId: string;
	/** Backend sletter bare ferdig behandlede sikringsordrer (Fullført eller Feilet). */
	deletable: boolean;
	hasContract: boolean;
	onDeleted: () => void;
}

function DeleteSection({ orderId, deletable, hasContract, onDeleted }: Readonly<DeleteSectionProps>) {
	const [confirming, setConfirming] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const deleteMutation = useStrombestillingApiMutation(`/sikringsordre/${orderId}`, ["sikringsordre"], "DELETE", true);

	async function deleteOrder() {
		setErrorMessage(null);
		try {
			const response = await deleteMutation.mutateAsync(undefined);
			if (!response.ok) {
				setErrorMessage(deleteErrorMessage(response.status));
				return;
			}
			onDeleted();
		} catch {
			setErrorMessage(deleteErrorMessage(0));
		}
	}

	return (
		<Section>
			<SectionTitle>Slett sikringsordre</SectionTitle>
			{!deletable ? (
				<EmptyText>Sikringsordren kan slettes når den er ferdig behandlet (Fullført eller Feilet).</EmptyText>
			) : confirming ? (
				<>
					<EmptyText>
						Er du sikker? Sikringer vil bli slettet i UtilityCloud.
						{hasContract && " Hovedprodukt og strømkontrakt vil ikke påvirkes."}
					</EmptyText>
					{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
					<ButtonRow>
						<Button
							type="button"
							label="Slett"
							colors={DANGER}
							loading={deleteMutation.isPending}
							disabled={deleteMutation.isPending}
							onClick={deleteOrder}
						/>
						<Button type="button" secondary label="Avbryt" disabled={deleteMutation.isPending} onClick={() => setConfirming(false)} />
					</ButtonRow>
				</>
			) : (
				<Button type="button" secondary colors={DANGER_SECONDARY} label="Slett sikringsordre" onClick={() => setConfirming(true)} />
			)}
		</Section>
	);
}

function qualityCheckErrorMessage(status: number): string {
	switch (status) {
		case 403:
			return "Sikringsordren kan ikke kvalitetssikres av den som opprettet den.";
		case 404:
			return "Fant ikke sikringsordren. Den kan være slettet.";
		default:
			return "Klarte ikke å oppdatere kvalitetssikringen. Prøv igjen senere.";
	}
}

/**
 * Hvorfor sikringsordren ikke kan kvalitetssikres av innlogget bruker, eller null når den kan det. Bare fullførte
 * sikringsordrer gir mening å kvalitetssikre, og backend avviser den som opprettet ordren.
 */
function qualityCheckBlocker(status: Sikringsordrestatus, createdByCurrentUser: boolean, canEdit: boolean): string | null {
	if (status !== Sikringsordrestatus.Completed) return `Bare fullførte sikringsordrer kan kvalitetssikres. Denne har status «${STATUS_LABEL[status]}».`;
	if (createdByCurrentUser) return "Du registrerte sikringsordren, så den må kvalitetssikres av noen andre.";
	if (!canEdit) return "Du har ikke tilgang til å kvalitetssikre sikringsordrer.";
	return null;
}

function useCurrentUserName(): string | null {
	const profile = useAuth().user?.profile;
	const name = [profile?.given_name, profile?.family_name].filter(Boolean).join(" ");
	return name || null;
}

interface QualityCheckSectionProps {
	orderId: string;
	status: Sikringsordrestatus;
	opprettetAv: string | null;
	kvalitetssikring: Kvalitetssikring | null;
	canEdit: boolean;
}

function QualityCheckSection({ orderId, status, opprettetAv, kvalitetssikring, canEdit }: Readonly<QualityCheckSectionProps>) {
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const mutation = useStrombestillingApiMutation(`/sikringsordre/${orderId}/kvalitetssikret`, ["sikringsordre"], "PUT", true);
	// Backend avviser kvalitetssikring fra den som opprettet ordren. Sjekkes her også, så knappen ikke tilbys forgjeves.
	const currentUserName = useCurrentUserName();
	const createdByCurrentUser = !!opprettetAv && opprettetAv === currentUserName;
	const blocker = qualityCheckBlocker(status, createdByCurrentUser, canEdit);

	async function setQualityChecked(kvalitetssikret: boolean) {
		setErrorMessage(null);
		try {
			const response = await mutation.mutateAsync({ kvalitetssikret });
			if (!response.ok) setErrorMessage(qualityCheckErrorMessage(response.status));
		} catch {
			setErrorMessage(qualityCheckErrorMessage(0));
		}
	}

	return (
		<Section>
			<SectionTitle>Kvalitetssikring</SectionTitle>
			{kvalitetssikring ? (
				<QualityCheckedText>
					<FontAwesomeIcon icon={faCircleCheck} color={colors.green} />
					Kvalitetssikret av {kvalitetssikring.navn}, {fmtDateTime(kvalitetssikring.tidspunkt)}
				</QualityCheckedText>
			) : (
				<EmptyText>{blocker ?? "Sikringsordren er ikke kvalitetssikret ennå."}</EmptyText>
			)}
			{errorMessage && <ErrorText>{errorMessage}</ErrorText>}
			{!blocker && (
				<ButtonRow>
					<Button
						type="button"
						secondary={!!kvalitetssikring}
						label={kvalitetssikring ? "Fjern kvalitetssikring" : "Marker som kvalitetssikret"}
						loading={mutation.isPending}
						disabled={mutation.isPending}
						onClick={() => setQualityChecked(kvalitetssikring ? false : true)}
					/>
				</ButtonRow>
			)}
		</Section>
	);
}

function Field({ name, value }: Readonly<{ name: string; value: ReactNode }>) {
	return (
		<>
			<FieldName>{name}</FieldName>
			<div>{value}</div>
		</>
	);
}

// Farger med dekning er tokenet med alfa i hex: 59 = 35 %, 33 = 20 %.
const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	background: ${colors.blue800}59;
	z-index: 40;
`;

const Panel = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 520px;
	max-width: 100vw;
	background: ${colors.white};
	z-index: 41;
	box-shadow: -4px 0 16px ${colors.grey900}33;
	padding: 28px 30px;
	overflow-y: auto;
	box-sizing: border-box;
`;

const TopRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 4px;
`;

const CloseLink = styled.a`
	font-size: 1.25rem;
	color: ${colors.grey900};
`;

const StatusExplanation = styled.p`
	margin: 0 0 20px;
	font-size: 0.875rem;
	color: ${colors.grey700};
	line-height: 1.5;
`;

const FieldGrid = styled.div`
	display: grid;
	grid-template-columns: 150px 1fr;
	row-gap: 12px;
	column-gap: 14px;
	font-size: 0.875rem;
	line-height: 1.5;
`;

const FieldName = styled.div`
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const Section = styled.div`
	margin-top: 24px;
	padding-top: 18px;
	box-shadow: inset 0 1px 0 ${colors.grey300};
`;

const SectionTitle = styled.div`
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
	margin-bottom: 10px;
`;

const EmptyText = styled.p`
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey700};
`;

const MonthList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const MonthRow = styled.div`
	display: grid;
	grid-template-columns: 90px 1fr;
	gap: 10px;
	align-items: center;
	font-size: 0.875rem;
	color: ${colors.grey800};
	padding: 8px 12px;
	border-radius: 6px;
	box-shadow: inset 0 0 0 1px ${colors.grey300};
`;

const Guid = styled.span`
	font-family: monospace;
	font-size: 0.75rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const TechnicalSummary = styled.summary`
	cursor: pointer;
	font-weight: 700;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const TechnicalList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-top: 10px;
`;

const TechnicalRow = styled.div`
	display: grid;
	grid-template-columns: 170px 1fr auto;
	gap: 10px;
	align-items: center;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const FailureBox = styled.div`
	border-radius: 9px;
	box-shadow: inset 0 0 0 1px ${colors.coral800};
	padding: 14px 18px;
	margin-bottom: 20px;
	font-size: 0.875rem;
	line-height: 1.5;
	color: ${colors.coral};
`;

const ButtonRow = styled.div`
	display: flex;
	gap: 8px;
	margin-top: 12px;
`;

const ErrorText = styled.p`
	margin: 10px 0 0;
	font-size: 0.875rem;
	color: ${colors.coral};
`;

const QualityCheckedText = styled.p`
	display: flex;
	align-items: center;
	gap: 8px;
	margin: 0;
	font-size: 0.875rem;
	color: ${colors.grey800};
`;

const FailureTitle = styled.div`
	font-weight: 700;
	margin-bottom: 4px;
`;
